import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TransformedCart } from "../../../../cart/types/cartApiTypes";
import Icon from "../../../../../../common/components/Icon/Icon";
import { decodeHtmlEntities } from "../../../../../../common/helpers/decodeHtmlEntities";

interface ItemsSummaryProps {
  cartData: TransformedCart | undefined;
}

const ITEMS_LIMIT = 2;

const ItemsSummary: React.FC<ItemsSummaryProps> = ({ cartData }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const items = cartData?.items || [];
  const hasMoreItems = items.length > ITEMS_LIMIT;

  // Determine which items to show based on state
  const visibleItems = isExpanded ? items : items.slice(0, ITEMS_LIMIT);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className="order-review__items">
      <h3 className="order-review__section-title">
        {t("orderReview.itemsTitle", "Order Items")}
        {/* Optional: Show total count in title */}
        <span className="order-review__count-badge">{items.length}</span>
      </h3>

      <div className="order-review__items-list">
        {visibleItems.map((item) => {
          const decodedName = decodeHtmlEntities(item.name);

          return (
            <div key={item.key} className="order-review__item">
              <div className="order-review__item-image">
                {item.images[0] ? (
                  <img src={item.images[0].thumbnail} alt={decodedName} />
                ) : (
                  <div className="placeholder-image" />
                )}
                <span className="order-review__item-qty">{item.quantity}</span>
              </div>
              <div className="order-review__item-details">
                <span className="order-review__item-name">{decodedName}</span>
                {item.variation.length > 0 && (
                  <span className="order-review__item-meta">
                    {item.variation
                      .map(
                        (v) => `${v.attribute}: ${decodeHtmlEntities(v.value)}`
                      )
                      .join(", ")}
                  </span>
                )}
              </div>
              <div className="order-review__item-price">
                {item.formattedCalculatedLineTotal}
              </div>
            </div>
          );
        })}
      </div>

      {/* Expand/Collapse Button */}
      {hasMoreItems && (
        <button
          type="button"
          className="order-review__toggle-btn"
          onClick={toggleExpand}
          aria-expanded={isExpanded}
        >
          <span>
            {isExpanded
              ? t("orderReview.showLess", "Show Less")
              : t("orderReview.showMore", {
                  count: items.length - ITEMS_LIMIT,
                  defaultValue: `Show ${items.length - ITEMS_LIMIT} more items`,
                })}
          </span>
          <Icon
            iconName="chevron-down"
            className={`icon--stroke-thick ${isExpanded ? "is-rotated" : ""}`}
          />
        </button>
      )}
    </div>
  );
};

export default ItemsSummary;
